import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Landmark, Banknote, ScrollText, HardHat, KeyRound,
  ArrowRight, Info, Building2, FileCheck,
} from "lucide-react";

const STEPS = [
  { icon: <Banknote size={20}/>,   title: "1 · You send your deposit",
    text: "Your 15% deposit lands directly into a segregated client-money account — separate from MAVULA's operating funds. It never mixes with company money." },
  { icon: <Landmark size={20}/>,   title: "2 · An independent trustee holds it",
    text: "The account is held with a regulated banking partner in the country where the property is located. An independent trustee/lawyer firm co-signs every release." },
  { icon: <HardHat size={20}/>,    title: "3 · Money is released against real milestones",
    text: "Funds only leave the client account when a verified build milestone is complete — land cleared, foundations, walls, roof, finishes. Every release is documented." },
  { icon: <FileCheck size={20}/>,  title: "4 · Photos + certificates prove each step",
    text: "You see dated photos and a certificate of works for every milestone inside your dashboard — before the next payment leaves the client account." },
  { icon: <KeyRound size={20}/>,   title: "5 · Title deed registered in your name",
    text: "On final payment, independent lawyers register the title deed in your name at the country's official property registry. No shared ownership. No leasehold." },
];

const NAMED = [
  { role: "Client escrow bank",         value: "Being confirmed per country · we will publish the bank name and IBAN before your first deposit." },
  { role: "Independent title trustee",  value: "Country-specific licensed law firm named on your project contract." },
  { role: "Independent verification",   value: "Third-party surveyor signs off every milestone before release." },
  { role: "Professional indemnity",     value: "Held with a regulated insurer; certificate provided on request." },
  { role: "Title insurance",            value: "Available per-property at your option; brokered independently." },
];

export default function HowYourMoneyIsHeldPage() {
  return (
    <main data-testid="money-held-page">
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Where your money sits</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98]">
            Your money, <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>protected in the open.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed">
            No sleight of hand. Every deposit is held in a <strong>segregated client-money account</strong> — separate from MAVULA — and only released against verified building milestones by an <strong>independent trustee</strong>.
          </p>
        </div>
      </section>

      {/* Flow */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ The pipeline</span></div>
        <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
          Five checkpoints. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Never one.</span>
        </h2>

        <ol className="mt-10 space-y-5" data-testid="money-held-steps">
          {STEPS.map((s, i) => (
            <li key={i} className="mv-card p-6 flex items-start gap-4" data-testid={`money-held-step-${i + 1}`}>
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mv-green-text shrink-0"
                   style={{ background: "rgba(212,175,55,0.16)" }}>{s.icon}</div>
              <div>
                <div className="font-display text-lg mv-green-text">{s.title}</div>
                <p className="mt-1 text-[14px]" style={{ color: "var(--mv-muted)" }}>{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Named third parties */}
      <section className="border-t mv-hairline py-14 md:py-20" style={{ background: "linear-gradient(180deg, #fff, rgba(212,175,55,0.06))" }}>
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Named third parties</span></div>
          <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-3">Nobody's word alone.</h2>
          <p className="mt-3 max-w-2xl text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
            Every one of these can be independently verified before you commit a single pound.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="money-held-named">
            {NAMED.map((n) => (
              <div key={n.role} className="mv-card p-5 flex items-start gap-3">
                <ShieldCheck size={18} className="mt-0.5" style={{ color: "#0A7A2C" }}/>
                <div>
                  <div className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{n.role}</div>
                  <div className="text-[14px] mt-1" style={{ color: "var(--mv-ink)" }}>{n.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA row */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/risk-realities" className="mv-card p-6 hover:shadow-md transition" data-testid="money-held-link-risk">
          <Info size={20} className="mv-green-text"/>
          <div className="font-display text-lg mv-green-text mt-3">Risk & Realities</div>
          <p className="text-[13.5px] mt-1" style={{ color: "var(--mv-muted)" }}>
            The plain-English list of what CAN go wrong — and how we mitigate each one.
          </p>
          <div className="mt-3 text-[13px] mv-green-text inline-flex items-center gap-1">Read <ArrowRight size={14}/></div>
        </Link>
        <Link to="/protections" className="mv-card p-6 hover:shadow-md transition" data-testid="money-held-link-protections">
          <ScrollText size={20} className="mv-green-text"/>
          <div className="font-display text-lg mv-green-text mt-3">Cooling-off & buy-back</div>
          <p className="text-[13.5px] mt-1" style={{ color: "var(--mv-muted)" }}>
            14-day cooling off plus optional buy-back at cost + earned interest.
          </p>
          <div className="mt-3 text-[13px] mv-green-text inline-flex items-center gap-1">Read <ArrowRight size={14}/></div>
        </Link>
        <a href="/api/investor-diligence-pack" target="_blank" rel="noreferrer" className="mv-card p-6 hover:shadow-md transition" data-testid="money-held-link-pack">
          <Building2 size={20} className="mv-green-text"/>
          <div className="font-display text-lg mv-green-text mt-3">Due-Diligence Pack (PDF)</div>
          <p className="text-[13.5px] mt-1" style={{ color: "var(--mv-muted)" }}>
            One-click PDF that bundles everything your lawyer needs — escrow flow, terms, risks, complaints.
          </p>
          <div className="mt-3 text-[13px] mv-green-text inline-flex items-center gap-1">Download <ArrowRight size={14}/></div>
        </a>
      </section>
    </main>
  );
}
